import { CommonModule } from "@angular/common";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDatepickerInputEvent, MatDatepickerModule } from "@angular/material/datepicker";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {
  MatDialog, MatDialogActions, MatDialogClose,
  MatDialogContent, MatDialogModule, MatDialogRef, MatDialogTitle
} from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatTableModule } from "@angular/material/table";
import { MatRadioChange, MatRadioModule } from "@angular/material/radio";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { CONSTANTS } from "../../../../Constants/Constants";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { CostReviewAddModifyReviewerComponent } from "../../common/cost-review-add-modify-reviewer/cost-review-add-modify-reviewer.component";
import { provideNativeDateAdapter } from "@angular/material/core";
import {
  CollaborationHistoryEnum,
  CostReviewDatabaseRecord,
  CostReviewerDetails,
  CostReviewOperation,
  CostReviewOperationResult,
  CostReviewStatus,
  ForcePublishDetails,
  SendReviewOrReplaceReviewerRequest,
  PolicyMandatedApproverRequest,
  ReviewerTypeItem
} from "../../../../models/dtos";
import { Subject, take, takeUntil } from "rxjs";
import { CommonDataService } from "../../../../services/common-data.service";
import { GetReviewDetailsService } from "../../../../services/api/get-review-details.service";
import { UserProfileService } from "../../../../services/common/user-profile.service";
import { IReportItem } from "../../../../models/reports/IReportItem";
import { ICostReviewRequestDialogData } from "../../../../models/common/ICostReviewRequestDialogData";
import { IReviewerDialogData } from "../../../../models/common/IReviewerDialogData";
import { IReviewerUpdateDialogResult } from "../../../../models/common/IReviewerUpdateDialogResult";
import { ICostReviewRequestCCRecipient } from "../../../../models/common/ICostReviewRequestCCRecipient";
import { CostReviewAddCcRecipient } from "../cost-review-add-cc-recipient/cost-review-add-cc-recipient";
import { CostReviewRecordBuilderService } from "../../../../services/review/cost-review-record-builder.service";
import { ConfirmationDialogService } from "../../../../services/common/confirmation-dialog.service";
import { NotificationMessageService } from "../../../../services/common/notification-message.service";
import { EstimateSaveService } from "../../../../services/estimate-save.service";
import { CloseOnEscapeDirective } from "../../../../directives/close-on-escape.directive";
import { ISubmittalPackages } from "../../../../models/properties/ISubmittalPackages";
import { GocCollaborationRestrictionHandlerService } from "../../../../services/common/goc-collaboration-restriction-handler.service";
import { MultiPdfReportDropdownComponent } from '../../../Common/multi-pdf-report-dropdown/multi-pdf-report-dropdown.component';
import { CommonMetaDataService } from "../../../../services/common-meta-data.service";
import { CommonFunctionsService } from "../../../../services/common-functions.service";

@Component({
  selector: 'app-cost-review-request-dialog',
  templateUrl: './cost-review-request.component.html',
  styleUrl: './cost-review-request.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButtonModule,
    MatDialogModule,
    MatIconModule,
    MatTableModule,
    MatRadioModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    MultiPdfReportDropdownComponent,
    MatDatepickerModule,
    CloseOnEscapeDirective
  ],
  providers: [provideNativeDateAdapter()]
})
export class CostReviewRequestDialog implements OnInit, OnDestroy {
  minDate: Date = new Date();
  isRequested: boolean = true;
  displayedColumns: string[] = ['role', 'reviewer', 'email', 'requiredBy'];
  displayedColumnsApprovers: string[] = ['role', 'reviewer', 'email', 'requiredBy', 'remove'];
  displayedColumnsCCRecipient: string[] = ['email', 'remove'];
  displayedColumnsCCTable:string[]= ['CC']
  dataSource: ICostReviewRequestDialogData[] = []
  dataSourceApprovers: ICostReviewRequestDialogData[] = [];
  dataSourceCCTable: string[]= ['CC1', 'CC2', 'CC3'];
  dataSourceCCRecipient: ICostReviewRequestCCRecipient[] = [
    { email: ''},
    { email: ''},
    { email: ''}
  ];
  selectedRow: ICostReviewRequestDialogData | null = null;
  reportsList: ISubmittalPackages[] = [];
  costReviewData: CostReviewDatabaseRecord;
  modificationNotes: string = '';
  reviewerList: ICostReviewRequestDialogData[] = [];
  private readonly destroy$ = new Subject<void>();
  costReviewRequestForm: FormGroup<{
    emailNote: FormControl<string>;
    emailRequestType: FormControl<string>;
    report1: FormControl<string>;
    report2: FormControl<string>;
    report3: FormControl<string>;
  }> = new FormGroup({
    emailNote: new FormControl<string>(CONSTANTS.EMPTY_STRING),
    emailRequestType: new FormControl<string>(CONSTANTS.ZERO_STRING, [Validators.required]),
    report1: new FormControl<string>({ value: CONSTANTS.EMPTY_STRING, disabled: true }),
    report2: new FormControl<string>({ value: CONSTANTS.EMPTY_STRING, disabled: true }),
    report3: new FormControl<string>({ value: CONSTANTS.EMPTY_STRING, disabled: true })
  });

  constructor(
    private readonly dialogRef: MatDialogRef<CostReviewRequestDialog>,
    private readonly dialog: MatDialog,
    private readonly commonDataService: CommonDataService,
    private readonly getReviewDetailsService: GetReviewDetailsService,
    private readonly translate: TranslateService,
    private readonly userProfileService: UserProfileService,
    private readonly costReviewRecordBuilderService: CostReviewRecordBuilderService,
    private readonly confirmationDialogService: ConfirmationDialogService,
    private readonly notificationMessageService: NotificationMessageService,
    private readonly estimateSaveService: EstimateSaveService,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly commonFunctionsService: CommonFunctionsService
  ) { }

  trackByReportId(index: number, report: IReportItem): number {
    return report.id;
  }

  ngOnInit(): void {
    this.costReviewData = this.costReviewRecordBuilderService.costReviewTableData;
    this.loadCcRecipientsFromPreferences(); 
    this.loadApproversFromEstimateData();

    if (this.costReviewData.status === CostReviewStatus.NotStarted
      && !this.costReviewData.costReviewers?.some(reviewer => reviewer.isPolicyMandatedApprover)) {
      this.loadPolicyMandatedApprovers();
    }
    this.setInitialSelectedApproverRow();
    this.updateVisibleReportItems();
  }

  private setInitialSelectedApproverRow(): void {
    if (this.dataSourceApprovers.length > CONSTANTS.ZERO) {
      this.selectedRow = this.dataSourceApprovers[CONSTANTS.ZERO];
    }
  }

  loadPolicyMandatedApprovers() {
    const estimateData = this.commonDataService.getEstimateData();

    const policyMandatedApproverRequest: PolicyMandatedApproverRequest = {
      sellPrice: this.commonDataService.getEstimateMetaData()?.estimateTab.soldASSellPrice || CONSTANTS.ZERO,
      subSegmentId: estimateData?.subSegmentID,
      salesOfficeId: estimateData?.salesOfficeId,
      cultureId: estimateData?.cultureID,
      agreementType: CONSTANTS.ZERO // This is required in Service agreement
    };

    this.getReviewDetailsService.getPolicyMandatedApprovers(policyMandatedApproverRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dataSource = response.costReviewers.map(reviewer => ({
            reviewerTypeId: reviewer.reviewerTypeID,
            role: reviewer.reviewerType,
            reviewer: reviewer.reviewerName,
            email: reviewer.reviewerEmail,
            requiredBy: reviewer.requiredBy ?? null,
            remove: false,
            originalReviewerId: reviewer.originalReviewerId,
            modifiedBy: reviewer.modifiedBy,
            userId: reviewer.reviewUserId,
            isPolicyMandatedApprover: true
          }));
          this.reviewerList = [this.dataSource, this.dataSourceApprovers].flat();
        },
        error: (error) => {
          console.error(error);
        }
      });
  }

  private updateVisibleReportItems(): void {
     this.reportsList = this.costReviewRecordBuilderService.getReportDropdownItems();
  }


  private loadApproversFromEstimateData(): void {
    if (this.dataSourceApprovers.length > 0) {
      return;
    }
    const costReviewerDataList: CostReviewerDetails[] = this.costReviewData.reviewObject?.reviewerDetailsForRequestPreference ?? [];
    const reviewersTypesDropdownList: ReviewerTypeItem[] = this.commonMetaDataService.getMetaData().costReviewerTypes ?? [];
    
    if (costReviewerDataList.length > 0) {
      const mappedReviewers: ICostReviewRequestDialogData[] = costReviewerDataList.map(reviewer => ({
        reviewerTypeId: reviewer.reviewerTypeID,
        role: reviewersTypesDropdownList.find(r => r.id === reviewer.reviewerTypeID)?.description || '',
        reviewer: reviewer.reviewerName,
        email: reviewer.reviewerEmail,
        requiredBy: reviewer.requiredBy ?? null,
        remove: false,
        originalReviewerId: reviewer.originalReviewerId,
        modifiedBy: reviewer.modifiedBy,
        userId: reviewer.reviewUserId,
        isPolicyMandatedApprover: reviewer.isPolicyMandatedApprover ?? false
      }));
      this.dataSource = mappedReviewers.filter(reviewer => reviewer.isPolicyMandatedApprover);
      this.dataSourceApprovers = mappedReviewers.filter(reviewer => !reviewer.isPolicyMandatedApprover);
    } else {
      const preferenceCostReviewers: CostReviewerDetails[] =
        structuredClone(this.commonDataService.getPreferenceCostReviewers());
      if (preferenceCostReviewers.length > 0) {
        this.dataSourceApprovers = preferenceCostReviewers.map(reviewer => ({
          role: reviewersTypesDropdownList.find(r => r.id === reviewer.reviewerTypeID)?.description || '',
          reviewer: reviewer.reviewerName,
          email: reviewer.reviewerEmail,
          requiredBy: reviewer.requiredBy ?? null,
          remove: false,
          originalReviewerId: reviewer.originalReviewerId,
          modifiedBy: reviewer.modifiedBy,
          reviewerTypeId: reviewer.reviewerTypeID,
          userId: reviewer.reviewUserId,
          isPolicyMandatedApprover: false
        }));
      }
      this.dataSource = [];
    }
    this.reviewerList = [this.dataSource, this.dataSourceApprovers].flat();
  }

  generateCostReviewersList(): CostReviewerDetails[] {
    this.reviewerList = [this.dataSource, this.dataSourceApprovers].flat();
    return this.reviewerList.map(reviewer => ({
      reviewerTypeID: reviewer.reviewerTypeId,
      reviewerType: reviewer.role,
      reviewerName: reviewer.reviewer,
      reviewerEmail: reviewer.email,
      reviewUserId: reviewer.userId,
      requiredBy: reviewer.requiredBy,
      closeDate: null as Date | null,
      reviewerStatus: CostReviewStatus.Pending,
      reviewerComments: '',
      originalReviewerId: reviewer.userId,
      modifiedBy: this.userProfileService.userInfo?.gid,
      originalReviewerName: reviewer.reviewer,
      isOriginalReviewerVisible: false,
      isPolicyMandatedApprover: reviewer.isPolicyMandatedApprover ?? false
    }));
  }

  getForcePublishDetails(): ForcePublishDetails | null {
    if (this.commonDataService.isCollaborationEstimate()) {
      this.reviewerList.forEach(reviewer => {
        this.modificationNotes += !reviewer.requiredBy ?
          this.translate.instant('COST_REVIEW_REVIEWER_NAME', { name: reviewer.reviewer }) :
          this.translate.instant('COST_REVIEW_REVIEWER_NAME_REQUIRED_BY', { name: reviewer.reviewer, date: reviewer.requiredBy });
      });
      this.modificationNotes = this.translate.instant('COST_REVIEW_SYSTEM_PUBLISHED', { modificationNotes: this.modificationNotes });
      return {
        forcePublishAdditionalInfo: CONSTANTS.ESTIMATE_FORCE_PUBLISH_COST_REVIEW_REQUEST,
        operation: CollaborationHistoryEnum.CostReviewRequested,
        modificationNotes: this.modificationNotes,
        estimateCheckoutAdditionalInfo: CONSTANTS.ESTIMATE_ADD_ON_COST_REVIEW_REQEST,
        isEstimateBeingCheckedOutAfterPublish: true
      }
    } else {
      return null;
    }

  }

  async generateSendRequestData(): Promise<SendReviewOrReplaceReviewerRequest> {
    this.costReviewData.requesterNotes = this.costReviewRequestForm.controls['emailNote'].value || CONSTANTS.EMPTY_STRING;
    this.costReviewData.autoSendEmail = this.costReviewRequestForm.controls['emailRequestType'].value === '1';
    this.costReviewData.reportOne = String(this.costReviewRequestForm.controls['report1'].value || CONSTANTS.NEGATIVE_ONE_STRING);
    this.costReviewData.reportTwo = String(this.costReviewRequestForm.controls['report2'].value || CONSTANTS.NEGATIVE_ONE_STRING);
    this.costReviewData.reportThree = String(this.costReviewRequestForm.controls['report3'].value || CONSTANTS.NEGATIVE_ONE_STRING);
    this.costReviewData.costReviewers = this.generateCostReviewersList();
    this.costReviewData.ccUser = this.costReviewRecordBuilderService.generateCCUserList();
    this.costReviewData.approvedMarginPercent =
      this.commonDataService.getCostReviewData()?.reviewObject?.costReviewMarginExceptions?.estimatedMargin ?? CONSTANTS.ZERO;
    this.costReviewData.approvedSellPrice = this.commonDataService.getEstimateMetaData()?.estimateTab.soldASSellPrice ?? CONSTANTS.ZERO;
    this.costReviewRecordBuilderService.salesPersonName = this.commonDataService.getReportHeaderData().salesPersonName;
    this.costReviewData.status = CostReviewStatus.InProcess;
    this.costReviewData.reviewObject = this.costReviewRecordBuilderService.getBusinessSpecificReviewObject();
    this.costReviewData.newReviewObject = this.costReviewRecordBuilderService.generateNewCostReviewAttributeRequestData();
    if (this.costReviewData.reviewObject) {
      this.costReviewData.reviewObject.reviewerDetailsForRequestPreference =
        this.costReviewData.costReviewers.filter(reviewer => !reviewer.isPolicyMandatedApprover);
    }
    this.costReviewData.tdcDollars = this.commonDataService.getEstimateMetaData()?.estimateTab.subTotalsCost ?? CONSTANTS.ZERO;
    //this.costReviewData.openDate => OpenDate is assigned in the backend as UTC; do not set it in the UI.
    const saveRequest = await this.estimateSaveService.createSaveEstimateRequest();

    const requestData: SendReviewOrReplaceReviewerRequest = {
      requestorName: this.costReviewRecordBuilderService.getCostReviewRequestorName(),
      cultureId: this.commonDataService.getEstimateCultureId() || CONSTANTS.ONE,
      operation: CostReviewOperation.SendReview,
      saveRequest,
      reports: await this.costReviewRecordBuilderService.getSelectedReportsPdf(
        this.costReviewRecordBuilderService.getSelectedReports(this.costReviewRequestForm)
      ),
      forcePublishDetails: this.getForcePublishDetails()
    };
    return requestData;
  }

  async onSend(): Promise<void> {
    if (this.costReviewRequestForm.valid) {
      const requestData = await this.generateSendRequestData();
      const isManualEmail = this.costReviewRequestForm.controls['emailRequestType'].value === CONSTANTS.ZERO_STRING;

      this.getReviewDetailsService.sendReviewOrReplaceReviewer(requestData)
        .pipe(take(CONSTANTS.ONE)).subscribe({
          next: (response) => {
            if (response?.result === CostReviewOperationResult.Success) {
              if (isManualEmail) {
                const recipientEmails = this.reviewerList.map(r => r.email);
                this.costReviewRecordBuilderService.openMailClient(response.emailSubject, response.emailBody, recipientEmails);
              }
              this.dialogRef.close({
                isRequested: true,
                costReviewData: this.costReviewData,
                requestorName: requestData.requestorName
              });
              if (this.commonDataService.isCollaborationEstimate() && response.collaborationHubData) {
                const collaborationHubData = response.collaborationHubData;
                this.commonFunctionsService.updateCollaborationHubData(collaborationHubData);
              }
              this.commonDataService.isSaveEnabled = false; 
            }
            else {
              this.getResponseBasedOnResultCode(response?.result);
            }
          },
          error: async (error) => {
            if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
              return;
            }
            console.error(error);
          }
        });
    }
  }

  private getResponseBasedOnResultCode(resultCode: CostReviewOperationResult): void {
    this.notificationMessageService.showNotificationMessageDialog(CONSTANTS.TYPE_ERROR, 'ERROR_TITLE', this.getErrorMessageKey(resultCode), CONSTANTS.DEFAULT_DIALOG_WIDTH);
  }

  private readonly getErrorMessageKey = (resultCode: CostReviewOperationResult): string => {
    // TODO: Add proper error messages for each result code
    switch (resultCode) {
      case CostReviewOperationResult.Failure:
        return 'COST_REVIEW_SEND_REQUEST_FAILED';
      case CostReviewOperationResult.EmailSendingFailure:
        return 'COST_REVIEW_EMAIL_SENDING_FAILED';
      case CostReviewOperationResult.GOCRestrictionApplicable:
        return 'COST_REVIEW_GOC_RESTRICTION_APPLICABLE';
      case CostReviewOperationResult.EstimateNoLongerCheckedOut:
        return 'COST_REVIEW_ESTIMATE_NO_LONGER_CHECKED_OUT';
      case CostReviewOperationResult.InvalidCostReviewer:
        return 'COST_REVIEW_INVALID_COST_REVIEWER';
      case CostReviewOperationResult.ForcePublishFailure:
        return 'COST_REVIEW_FORCE_PUBLISH_FAILED';
      default:
        return 'COST_REVIEW_SEND_REQUEST_FAILED';
    }
  }

  get formControls() {
    return this.costReviewRequestForm.controls;
  }

  get isSendDisabled(): boolean {
    return this.costReviewRequestForm.invalid || (this.dataSource.length === 0 && this.dataSourceApprovers.length === 0);
  }

  get isAddReviewerDisabled(): boolean {
    return this.dataSourceApprovers.length >= CONSTANTS.MAX_COST_REVIEWERS;
  }

  onRequestTypeChange(event: MatRadioChange): void {
    this.toggleReportControls(event.value);
  }

  private toggleReportControls(requestType: string): void {
    const controls = this.costReviewRequestForm.controls;
    if (requestType === '1') {
      controls.report1.enable();
      controls.report2.enable();
      controls.report3.enable();
    } else {
      controls.report1.disable();
      controls.report2.disable();
      controls.report3.disable();
    }
  }

  showReviewerDialog(isModify: boolean): void {
    if (!isModify && this.isAddReviewerDisabled) {
      return;
    }
    const existingEmails = [
      ...this.reviewerList.map(r => r.email)
    ];

    const policyMandatedApprovers : ICostReviewRequestDialogData[] = this.dataSource || [];
    const data: IReviewerDialogData = {
      isModifyMode: isModify,
      title: isModify ? 'MODIFY_REVIEWER' : 'ADD_REVIEWER',
      existingEmails: existingEmails,
      policyMandatedApprovers: policyMandatedApprovers
    };
    const dialogRef = this.dialog.open(CostReviewAddModifyReviewerComponent, {
      width: '600px',
      height: '280px',
      data: {dialogData: data, isCostReviewRequest: true},
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      takeUntil(this.destroy$)
    ).subscribe((result: ICostReviewRequestDialogData | IReviewerUpdateDialogResult | undefined) => {
      if (result) {
        if (isModify) {
          if (!this.isReviewerUpdateDialogResult(result)) {
            return;
          }
          const selectedReviewerIndex = this.dataSource.findIndex(
            reviewer => reviewer.email?.toLowerCase() === result.previousEmail?.toLowerCase()
          );
          if (selectedReviewerIndex !== -1) {
            this.dataSource[selectedReviewerIndex].reviewer = result.data.reviewer;
            this.dataSource[selectedReviewerIndex].email = result.data.email;
            this.dataSource[selectedReviewerIndex].userId = result.data.userId;
          } else {
            result.data.isPolicyMandatedApprover = true;
            this.dataSource.push(result.data);
          }
          this.dataSource = [...this.dataSource];  
        } else {
          if (this.isReviewerUpdateDialogResult(result)) {
            // Defensive guard: add dialog should never return update payload.
            return;
          }
          this.dataSourceApprovers.push(result);
          this.dataSourceApprovers = [...this.dataSourceApprovers];
        }
        this.reviewerList = [this.dataSource, this.dataSourceApprovers].flat();
      }
    });
  }

  private isReviewerUpdateDialogResult(result: ICostReviewRequestDialogData | IReviewerUpdateDialogResult): result is IReviewerUpdateDialogResult {
    return (result as IReviewerUpdateDialogResult).type === 'updated';
  }

  removeSelectedUser(reviewerName: string): void {
    const message = `${this.translate.instant('COST_REVIEW_DELETE_REVIEWER_CONFIRMATION')} "${reviewerName}"?`;
    this.confirmationDialogService.confirmWarning(message, 'DELETE_RECORD_CONFIRMATION')
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.dataSourceApprovers.forEach((approver, index) => {
            if (reviewerName === approver.reviewer) {
              this.dataSourceApprovers.splice(index, CONSTANTS.ONE);
            }
          });
          this.dataSourceApprovers = [...this.dataSourceApprovers];
          this.reviewerList = [this.dataSource, this.dataSourceApprovers].flat();
        }
      });
  }

  showCCRecipientDialog(): void {
    const dialogRef = this.dialog.open(CostReviewAddCcRecipient, {
      width: '600px',
      height: '220px',
      data: {
        dataSourceCCRecipient: this.dataSourceCCRecipient,
        isCostReviewRequest: true
      },
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
    takeUntil(this.destroy$)).subscribe((email: string) => {
    if (email) {
      const emptyIndex = this.dataSourceCCRecipient.findIndex(item => item.email === CONSTANTS.EMPTY_STRING);
      if (emptyIndex !== -1) {
        this.dataSourceCCRecipient[emptyIndex] = { email };
        this.dataSourceCCRecipient = [...this.dataSourceCCRecipient];
        this.costReviewRecordBuilderService.dataSourceCCRecipient = this.dataSourceCCRecipient;
      }
    }
    });
  }

  isValidRowSelected(element: ICostReviewRequestCCRecipient) : boolean {
    return this.selectedRow === element && element.email.length > CONSTANTS.ZERO;
  }

  removeSelectedCCRecipient(recipientEmail: string): void {
    const filled = this.dataSourceCCRecipient.filter(
      item => item.email !== recipientEmail && item.email !== CONSTANTS.EMPTY_STRING
    );
    while (filled.length < CONSTANTS.THREE) {
      filled.push({ email: CONSTANTS.EMPTY_STRING });
    }
    this.selectedRow = null;
    this.dataSourceCCRecipient = [...filled];
    this.costReviewRecordBuilderService.dataSourceCCRecipient = this.dataSourceCCRecipient;
  }

  get addCcRecipientEnabled(): boolean {
    if(this.dataSourceCCRecipient.filter(item => item.email === CONSTANTS.EMPTY_STRING).length === CONSTANTS.ZERO) {
      return false;
    }
      return true;
  }

  loadCcRecipientsFromPreferences(): void {
    const userPreferences = this.commonDataService.getPreferenceData();
    if (!userPreferences?.costReviewerPreferences) {
      return;
    }
    const costReviewerCcPrefs = userPreferences.costReviewerPreferences.crEmailCCUsers;

    if (costReviewerCcPrefs && costReviewerCcPrefs.length > CONSTANTS.ZERO) {
      this.dataSourceCCRecipient = costReviewerCcPrefs.map(email => ({ email }));  
      while(this.dataSourceCCRecipient.length < CONSTANTS.THREE) {
        this.dataSourceCCRecipient.push({ email: CONSTANTS.EMPTY_STRING });
      }
      this.costReviewRecordBuilderService.dataSourceCCRecipient = this.dataSourceCCRecipient;
    }
  }
  
  onRequiredByDateChange(event: MatDatepickerInputEvent<Date>, element: ICostReviewRequestDialogData): void {
    const selectedDate = event.value;
    const requiredByDate = selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 12) : null;
    element.requiredBy = requiredByDate;
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.costReviewRequestForm.reset();
    this.dataSource = [];
    this.dataSourceApprovers = [];
    this.dataSourceCCRecipient = [];
    this.reviewerList = [];
    this.costReviewData = null;
  }
}